import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";

// Session-only, in-memory notification store. Mirrors the old CRM's notification
// box: fed live from Socket.IO events, never persisted (cleared on reload), no API.
export type NotificationKind =
  | "order"
  | "order_fulfillment"
  | "activity"
  | "reply"
  | "status"
  | "warning"
  | "missing"
  | "import"
  | "error";

export interface NotificationItem {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string;
  path?: string | null;
  // Id the row exposes through its copy action (activity / inquiry / complaint id).
  copyId?: string | number | null;
  createdAt: number;
  read: boolean;
}

// New notifications supply everything except the bookkeeping fields.
export type NewNotification = Omit<
  NotificationItem,
  "id" | "createdAt" | "read"
>;

// Keep the list bounded so a long session can't grow it without limit.
const MAX_ITEMS = 50;

type Action =
  | { type: "add"; payload: NotificationItem }
  | { type: "markRead"; id: string }
  | { type: "markAllRead" }
  | { type: "clearAll" };

const reducer = (
  state: NotificationItem[],
  action: Action
): NotificationItem[] => {
  switch (action.type) {
    case "add":
      return [action.payload, ...state].slice(0, MAX_ITEMS);
    case "markRead":
      return state.map((n) =>
        n.id === action.id ? { ...n, read: true } : n
      );
    case "markAllRead":
      return state.map((n) => (n.read ? n : { ...n, read: true }));
    case "clearAll":
      return [];
    default:
      return state;
  }
};

interface NotificationsContextValue {
  items: NotificationItem[];
  unreadCount: number;
  add: (payload: NewNotification) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  clearAll: () => void;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(
  null
);

export const NotificationsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [items, dispatch] = useReducer(reducer, []);

  // dispatch identity is stable, so these callbacks never change — safe to list
  // as effect dependencies in consumers without re-subscribing socket listeners.
  const add = useCallback((payload: NewNotification) => {
    dispatch({
      type: "add",
      payload: {
        ...payload,
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        createdAt: Date.now(),
        read: false,
      },
    });
  }, []);

  const markRead = useCallback((id: string) => {
    dispatch({ type: "markRead", id });
  }, []);

  const markAllRead = useCallback(() => {
    dispatch({ type: "markAllRead" });
  }, []);

  const clearAll = useCallback(() => {
    dispatch({ type: "clearAll" });
  }, []);

  const unreadCount = useMemo(
    () => items.reduce((n, item) => (item.read ? n : n + 1), 0),
    [items]
  );

  const value = useMemo(
    () => ({ items, unreadCount, add, markRead, markAllRead, clearAll }),
    [items, unreadCount, add, markRead, markAllRead, clearAll]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotificationsStore = () => {
  const ctx = useContext(NotificationsContext);
  if (!ctx)
    throw Error(
      "useNotificationsStore must be used within a NotificationsProvider"
    );
  return ctx;
};
